import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { repTargetsAPI, adminAPI } from '../api/client';
import { CURRENCY } from '../config/constants';
import { istYearMonth, formatIstMonthYear, formatVolumeM3 } from '../utils/formatIst';
import { useGlobalSearch } from '../context/SearchContext';
import { matchesGlobalSearch } from '../utils/globalSearch';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  BarChart3,
  RefreshCw,
  Calendar,
  TrendingUp,
  Target,
  Users,
  IndianRupee,
  AlertTriangle,
  MapPin,
  CheckCircle2,
  Truck,
  Search,
  ShoppingCart,
  Clock,
  CalendarCheck,
} from 'lucide-react';

const EMPTY_EXTRAS = {
  orders: {
    order_count: 0,
    revenue_inr: 0,
    pending_count: 0,
    confirmed_count: 0,
    delivered_count: 0,
    cancelled_count: 0,
  },
  attendance: {
    punch_sessions: 0,
    rep_days_present: 0,
    reps_with_punch: 0,
    currently_on_duty: 0,
  },
  leave: {
    pending_requests: 0,
    approved_requests: 0,
    rejected_requests: 0,
    approved_leave_days: 0,
    reps_with_approved_leave: 0,
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ACHIEVEMENT_FILTERS = [
  { id: 'all', label: 'All reps' },
  { id: 'met', label: 'Met target' },
  { id: 'below', label: 'Below target' },
  { id: 'no_target', label: 'No target' },
];

function formatTrend(today, yesterday) {
  const t = Number(today) || 0;
  const y = Number(yesterday) || 0;
  if (t === y) return { label: 'Same as yesterday', tone: 'text-slate-500' };
  const diff = t - y;
  const sign = diff > 0 ? '+' : '';
  return {
    label: `${sign}${diff} vs yesterday`,
    tone: diff > 0 ? 'text-emerald-600' : 'text-rose-600',
  };
}

function shortName(name, max = 14) {
  const n = String(name || '').trim();
  if (n.length <= max) return n || '—';
  return `${n.slice(0, max - 1)}…`;
}

function isDeadlockError(err) {
  const msg = String(err?.response?.data?.error || err?.message || '');
  return /deadlock|try restarting transaction/i.test(msg);
}

async function withDeadlockRetry(fn, { retries = 2, baseDelayMs = 350 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isDeadlockError(err) || attempt >= retries) throw err;
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * (attempt + 1)));
    }
  }
  throw lastErr;
}

const Analytics = () => {
  const [selectedMonth, setSelectedMonth] = useState(istYearMonth());
  const [teamRows, setTeamRows] = useState([]);
  const [overview, setOverview] = useState(null);
  const [monthExtras, setMonthExtras] = useState(EMPTY_EXTRAS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [achievementFilter, setAchievementFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { query: globalSearch } = useGlobalSearch();

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Month summary is the heaviest query — load first to avoid overlapping with other analytics reads.
      const summaryRes = await withDeadlockRetry(() =>
        repTargetsAPI.getMonthSummary(selectedMonth)
      );
      setTeamRows(summaryRes.data?.data || []);

      const [overviewRes, extrasRes] = await Promise.all([
        withDeadlockRetry(() => adminAPI.getOverviewStats()),
        withDeadlockRetry(() => adminAPI.getAnalyticsMonthExtras({ month: selectedMonth })),
      ]);
      setOverview(overviewRes.data?.data || null);
      const extras = extrasRes.data?.data || {};
      setMonthExtras({
        orders: { ...EMPTY_EXTRAS.orders, ...(extras.orders || {}) },
        attendance: { ...EMPTY_EXTRAS.attendance, ...(extras.attendance || {}) },
        leave: { ...EMPTY_EXTRAS.leave, ...(extras.leave || {}) },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Failed to load analytics';
      setError(
        isDeadlockError(err)
          ? `${msg} — tap Refresh to retry (server may be busy).`
          : msg
      );
      setTeamRows([]);
      setOverview(null);
      setMonthExtras(EMPTY_EXTRAS);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const teamStats = useMemo(() => {
    const withTarget = teamRows.filter((r) => r.has_target);
    const met = withTarget.filter((r) => (r.achievement_percent ?? 0) >= 100);
    const below = withTarget.filter((r) => (r.achievement_percent ?? 0) < 100);
    const noTarget = teamRows.filter((r) => !r.has_target);

    const totalTarget = teamRows.reduce((s, r) => s + (parseFloat(r.target_m3) || 0), 0);
    const totalAchieved = teamRows.reduce((s, r) => s + (parseFloat(r.achieved_m3) || 0), 0);
    const totalBonus = teamRows.reduce((s, r) => s + (parseFloat(r.bonus_earned) || 0), 0);
    const totalPenalty = teamRows.reduce((s, r) => s + (parseFloat(r.penalty_amount) || 0), 0);
    const totalNet = teamRows.reduce((s, r) => s + (parseFloat(r.net_compensation) || 0), 0);

    const avgAchieved =
      teamRows.length > 0
        ? totalAchieved / teamRows.length
        : 0;
    const teamPct =
      totalTarget > 0 ? Math.round((totalAchieved / totalTarget) * 100) : null;
    const avgPct = withTarget.length
      ? Math.round(
          withTarget.reduce((s, r) => s + (r.achievement_percent ?? 0), 0) / withTarget.length
        )
      : null;

    return {
      repCount: teamRows.length,
      withTargetCount: withTarget.length,
      metCount: met.length,
      belowCount: below.length,
      noTargetCount: noTarget.length,
      totalTarget,
      totalAchieved,
      teamPct,
      avgAchieved,
      avgPct,
      totalBonus,
      totalPenalty,
      totalNet,
    };
  }, [teamRows]);

  const filteredRows = useMemo(() => {
    return teamRows
      .filter((row) => {
        if (achievementFilter === 'met' && (!row.has_target || (row.achievement_percent ?? 0) < 100)) {
          return false;
        }
        if (achievementFilter === 'below' && (!row.has_target || (row.achievement_percent ?? 0) >= 100)) {
          return false;
        }
        if (achievementFilter === 'no_target' && row.has_target) return false;
        return true;
      })
      .filter((row) =>
        matchesGlobalSearch(globalSearch, [
          row.rep_name,
          row.target_m3,
          row.achieved_m3,
          row.achievement_percent,
          row.bonus_earned,
          row.penalty_amount,
        ])
      )
      .filter((row) =>
        searchTerm.trim() ? matchesGlobalSearch(searchTerm, [row.rep_name]) : true
      )
      .sort((a, b) => (b.achieved_m3 || 0) - (a.achieved_m3 || 0));
  }, [teamRows, achievementFilter, globalSearch, searchTerm]);

  const chartReps = useMemo(() => {
    return [...teamRows]
      .filter((r) => r.has_target || (r.achieved_m3 || 0) > 0)
      .sort((a, b) => (b.achieved_m3 || 0) - (a.achieved_m3 || 0))
      .slice(0, 12);
  }, [teamRows]);

  const barChartData = useMemo(
    () => ({
      labels: chartReps.map((d) => shortName(d.rep_name)),
      datasets: [
        {
          label: 'Achieved (m³)',
          data: chartReps.map((d) => parseFloat(d.achieved_m3) || 0),
          backgroundColor: 'rgba(16, 185, 129, 0.85)',
          borderRadius: 4,
        },
        {
          label: 'Target (m³)',
          data: chartReps.map((d) => parseFloat(d.target_m3) || 0),
          backgroundColor: 'rgba(148, 163, 184, 0.55)',
          borderRadius: 4,
        },
      ],
    }),
    [chartReps]
  );

  const statusChartData = useMemo(
    () => ({
      labels: ['Met target', 'Below target', 'No target set'],
      datasets: [
        {
          data: [
            teamStats.metCount,
            teamStats.belowCount,
            teamStats.noTargetCount,
          ],
          backgroundColor: [
            'rgba(16, 185, 129, 0.9)',
            'rgba(245, 158, 11, 0.9)',
            'rgba(148, 163, 184, 0.7)',
          ],
          borderWidth: 0,
        },
      ],
    }),
    [teamStats]
  );

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: {
        display: true,
        text: 'Top reps — volume vs target (m³)',
        font: { size: 14 },
      },
    },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'm³' } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      title: {
        display: true,
        text: 'Target status (reps)',
        font: { size: 14 },
      },
    },
  };

  const orders = monthExtras.orders;
  const attendance = monthExtras.attendance;
  const leave = monthExtras.leave;

  const visitsTrend = formatTrend(overview?.visits_today, overview?.visits_yesterday);
  const distanceTrend = formatTrend(
    overview?.distance_km_today,
    overview?.distance_km_yesterday
  );
  const alertsTrend = formatTrend(
    overview?.security_alerts_today,
    overview?.security_alerts_yesterday
  );

  if (loading && !teamRows.length) {
    return (
      <div className="admin-page flex items-center justify-center min-h-64 text-slate-500">
        <RefreshCw className="w-6 h-6 animate-spin mr-2 text-blue-600" />
        Loading analytics…
      </div>
    );
  }

  return (
    <div className="admin-page space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="flex items-start gap-3">
          <BarChart3 className="w-7 h-7 text-blue-600 shrink-0 mt-0.5" />
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Analytics</h2>
            <p className="text-sm text-slate-500">
              Live sales performance and field activity for {formatIstMonthYear(selectedMonth)}.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Month (IST)</label>
            <div className="relative">
              <Calendar className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={fetchAnalytics}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm">
          {error}
        </div>
      )}

      {/* Sales & compensation */}
      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Sales & compensation
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={Target}
            label="Team achieved / target"
            value={`${formatVolumeM3(teamStats.totalAchieved)} / ${formatVolumeM3(teamStats.totalTarget)} m³`}
            sub={teamStats.teamPct != null ? `${teamStats.teamPct}% of team target` : 'No targets set'}
            accent="blue"
          />
          <KpiCard
            icon={TrendingUp}
            label="Avg achievement"
            value={teamStats.avgPct != null ? `${teamStats.avgPct}%` : '—'}
            sub={`${teamStats.metCount} met · ${teamStats.belowCount} below`}
            accent="emerald"
          />
          <KpiCard
            icon={IndianRupee}
            label="Bonus (month)"
            value={CURRENCY.format(teamStats.totalBonus)}
            sub={`Penalty ${CURRENCY.format(teamStats.totalPenalty)}`}
            accent="green"
          />
          <KpiCard
            icon={Users}
            label="Active reps"
            value={String(teamStats.repCount)}
            sub={`${teamStats.withTargetCount} with target · ${teamStats.noTargetCount} without`}
            accent="purple"
          />
        </div>
      </section>

      {/* Orders (selected month) */}
      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Orders ({formatIstMonthYear(selectedMonth)})
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={ShoppingCart}
            label="Order revenue"
            value={CURRENCY.format(orders.revenue_inr)}
            sub={`${orders.order_count} orders in month`}
            accent="blue"
          />
          <KpiCard
            icon={ShoppingCart}
            label="Delivered"
            value={String(orders.delivered_count)}
            sub={`Pending ${orders.pending_count} · Confirmed ${orders.confirmed_count}`}
            accent="emerald"
          />
          <KpiCard
            icon={IndianRupee}
            label="Open pipeline"
            value={String(orders.pending_count + orders.confirmed_count)}
            sub={
              orders.cancelled_count > 0
                ? `${orders.cancelled_count} cancelled`
                : 'Pending + confirmed orders'
            }
            accent="amber"
          />
          <KpiCard
            icon={TrendingUp}
            label="Avg order value"
            value={
              orders.order_count > 0
                ? CURRENCY.format(orders.revenue_inr / orders.order_count)
                : '—'
            }
            sub="Revenue ÷ order count"
            accent="slate"
          />
        </div>
      </section>

      {/* Attendance & leave (selected month) */}
      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Attendance & leave ({formatIstMonthYear(selectedMonth)})
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={Clock}
            label="Attendance days"
            value={String(attendance.rep_days_present)}
            sub={`${attendance.reps_with_punch} reps · ${attendance.punch_sessions} punch sessions`}
            accent="indigo"
          />
          <KpiCard
            icon={Users}
            label="On duty now"
            value={String(attendance.currently_on_duty)}
            sub="Active punch-in (live)"
            accent="green"
          />
          <KpiCard
            icon={CalendarCheck}
            label="Approved leave days"
            value={String(leave.approved_leave_days)}
            sub={`${leave.reps_with_approved_leave} reps · ${leave.approved_requests} requests`}
            accent="purple"
          />
          <KpiCard
            icon={CalendarCheck}
            label="Leave pending"
            value={String(leave.pending_requests)}
            sub={`Rejected ${leave.rejected_requests} this month`}
            accent="rose"
          />
        </div>
      </section>

      {/* Field operations (today) */}
      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Field operations (today)
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={Users}
            label="Executives online"
            value={
              overview
                ? `${overview.active_executives?.online ?? 0}/${overview.active_executives?.total ?? 0}`
                : '—'
            }
            sub={
              overview?.active_executives?.on_duty
                ? `${overview.active_executives.on_duty} on duty`
                : 'Live GPS + attendance'
            }
            accent="indigo"
          />
          <KpiCard
            icon={CheckCircle2}
            label="Visits today"
            value={String(overview?.visits_today ?? 0)}
            sub={visitsTrend.label}
            subClass={visitsTrend.tone}
            accent="emerald"
          />
          <KpiCard
            icon={Truck}
            label="Distance today"
            value={`${overview?.distance_km_today ?? 0} km`}
            sub={distanceTrend.label}
            subClass={distanceTrend.tone}
            accent="slate"
          />
          <KpiCard
            icon={AlertTriangle}
            label="Security alerts"
            value={String(overview?.security_alerts_today ?? 0)}
            sub={alertsTrend.label}
            subClass={alertsTrend.tone}
            accent="rose"
          />
        </div>
      </section>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm p-4 h-80">
          {chartReps.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">
              No volume data for this month yet.
            </div>
          ) : (
            <Bar options={barOptions} data={barChartData} />
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 h-80">
          {teamStats.repCount === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">
              No reps in scope.
            </div>
          ) : (
            <Doughnut options={doughnutOptions} data={statusChartData} />
          )}
        </div>
      </div>

      {/* Performance table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm admin-table-shell">
        <div className="p-4 border-b border-slate-100 flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Rep performance</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Achieved m³ from orders (pending, confirmed, delivered). Bonus/penalty from saved progress.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="relative w-full min-w-0 sm:min-w-[12rem] sm:max-w-xs">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search rep…"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {ACHIEVEMENT_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setAchievementFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    achievementFilter === f.id
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="admin-table-shell">
          <div className="admin-table-scroll">
            <table className="admin-data-table-wide text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Rep</th>
                  <th className="px-4 py-3 text-right font-semibold">Target (m³)</th>
                  <th className="px-4 py-3 text-right font-semibold">Achieved (m³)</th>
                  <th className="px-4 py-3 text-center font-semibold">%</th>
                  <th className="px-4 py-3 text-right font-semibold">Bonus</th>
                  <th className="px-4 py-3 text-right font-semibold">Penalty</th>
                  <th className="px-4 py-3 text-right font-semibold">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-10 text-center text-slate-500">
                      No reps match your filters.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((rep) => {
                    const target = parseFloat(rep.target_m3) || 0;
                    const achieved = parseFloat(rep.achieved_m3) || 0;
                    const pct = rep.achievement_percent;
                    const met = rep.has_target && pct != null && pct >= 100;
                    const bonus = parseFloat(rep.bonus_earned) || 0;
                    const penalty = parseFloat(rep.penalty_amount) || 0;
                    const net = parseFloat(rep.net_compensation) || bonus - penalty;

                    return (
                      <tr key={rep.rep_id} className="hover:bg-slate-50/80">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900">{rep.rep_name}</div>
                          {!rep.has_target && (
                            <span className="text-xs text-slate-400">No target set</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-700">
                          {rep.has_target ? formatVolumeM3(target) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-slate-900">
                          {formatVolumeM3(achieved)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {rep.has_target && pct != null ? (
                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold text-white ${
                                met ? 'bg-emerald-600' : 'bg-amber-500'
                              }`}
                            >
                              {pct}%
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-emerald-700 font-medium">
                          {bonus > 0 ? CURRENCY.format(bonus) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-rose-600 font-medium">
                          {penalty > 0 ? CURRENCY.format(penalty) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900">
                          {CURRENCY.format(net)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" />
          Respects your division/reportee visibility. Sync sales from orders in Target Management if bonus figures look stale.
        </div>
      </div>
    </div>
  );
};

function KpiCard({ icon: Icon, label, value, sub, subClass = 'text-slate-500', accent }) {
  const accents = {
    blue: 'border-l-blue-500',
    emerald: 'border-l-emerald-500',
    green: 'border-l-green-500',
    purple: 'border-l-purple-500',
    indigo: 'border-l-indigo-500',
    slate: 'border-l-slate-400',
    rose: 'border-l-rose-500',
  };

  return (
    <div
      className={`bg-white rounded-xl border border-slate-100 shadow-sm p-4 border-l-4 ${
        accents[accent] || accents.slate
      }`}
    >
      <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <p className="text-xl font-bold text-slate-900 leading-tight">{value}</p>
      <p className={`text-xs mt-1.5 ${subClass}`}>{sub}</p>
    </div>
  );
}

export default Analytics;
